import { frozenThemeTokens, themeTokenKeys, type ThemeToken } from "./themeTokens.generated";
import { isThemeId, themeCatalog, themeDefinition, themeIdOf, themeIds, themeVarNames, type ThemeBrightness, type ThemeDefinition, type ThemeFamily, type ThemeId } from "./themeCatalog";

const LEGACY_MODE_DARK = "dark";
const LEGACY_MODE_LIGHT = "light";

/** Wirksame Theme-Vorgaben der App für die aktuelle Sitzung (Antwort des Produkt-Backends). */
export type ThemeAvailability = {
  availableThemeIds: ThemeId[];
  appDefaultThemeId: ThemeId;
  tenantDefaultThemeId: ThemeId | null;
  effectiveDefaultThemeId: ThemeId;
};

/** Freigabe der App (Systemadministration). */
export type AppThemeSettings = {
  availableThemeIds: ThemeId[];
  defaultThemeId: ThemeId;
  lastUpdatedAt?: string | null;
};

/** Liest die Backend-Antwort; unbekannte Kennungen (neuere Server) fallen weg. */
export function parseThemeAvailability(raw: unknown): ThemeAvailability | null {
  if (!raw || typeof raw !== "object") return null;
  const value = raw as Record<string, unknown>;
  const available = Array.isArray(value.availableThemeIds) ? value.availableThemeIds.filter(isThemeId) : [];
  if (available.length === 0 || !isThemeId(value.appDefaultThemeId) || !isThemeId(value.effectiveDefaultThemeId)) return null;
  return {
    availableThemeIds: available,
    appDefaultThemeId: value.appDefaultThemeId,
    tenantDefaultThemeId: isThemeId(value.tenantDefaultThemeId) ? value.tenantDefaultThemeId : null,
    effectiveDefaultThemeId: value.effectiveDefaultThemeId,
  };
}

export function parseAppThemeSettings(raw: unknown): AppThemeSettings | null {
  if (!raw || typeof raw !== "object") return null;
  const value = raw as Record<string, unknown>;
  const available = Array.isArray(value.availableThemeIds) ? value.availableThemeIds.filter(isThemeId) : [];
  if (available.length === 0 || !isThemeId(value.defaultThemeId)) return null;
  return { availableThemeIds: available, defaultThemeId: value.defaultThemeId, lastUpdatedAt: typeof value.lastUpdatedAt === "string" ? value.lastUpdatedAt : null };
}

/** Auswahlreihenfolge: Benutzer → Mandant → App → erstes freigegebenes Theme. */
export function resolveThemeId({ userThemeId, availability, productDefaultThemeId }: { userThemeId: ThemeId | null; availability: ThemeAvailability | null; productDefaultThemeId: ThemeId }): ThemeId {
  const available: readonly ThemeId[] = availability?.availableThemeIds ?? themeIds;
  if (userThemeId && available.includes(userThemeId)) return userThemeId;
  const fallback = availability?.effectiveDefaultThemeId ?? productDefaultThemeId;
  if (available.includes(fallback)) return fallback;
  return available[0] ?? productDefaultThemeId;
}

const legacyPrimaryFamilies: Record<string, ThemeFamily> = { blue: "classic", purple: "violet", green: "green", cyan: "teal" };

/**
 * Bildet die frühere Auswahl (Stil, Hell/Dunkel, Primärfarbe) auf ein Theme ab. Frühere Farben ohne
 * eigenes Theme (Orange, Rot) erhalten die Produktfamilie in derselben Helligkeit.
 */
export function legacyThemeId(legacy: { mode?: unknown; style?: unknown; primary?: unknown }, productDefaultThemeId: ThemeId): ThemeId {
  const productFamily = themeDefinition(productDefaultThemeId).family;
  const style = typeof legacy.style === "string" && ["noir", "kraftraum", "classic"].includes(legacy.style) ? legacy.style as ThemeFamily : null;
  const family = style ?? (typeof legacy.primary === "string" ? legacyPrimaryFamilies[legacy.primary] : undefined) ?? productFamily;
  const brightness: ThemeBrightness = legacy.mode === LEGACY_MODE_DARK ? "dark" : legacy.mode === LEGACY_MODE_LIGHT ? "light" : themeDefinition(productDefaultThemeId).brightness;
  return themeIdOf(family, brightness) ?? themeIdOf(family, "light") ?? productDefaultThemeId;
}

const VAR_STYLE_ID = "mq-theme-vars";

const UNITLESS_TOKEN = /^(lineHeight|fontWeight|zIndex|opacity)/;

/**
 * Design-Tokens als CSS-Variablen `--ant-*` (Namen wie bisher von Antd erzeugt, z. B. `colorPrimary` →
 * `--ant-color-primary`, `borderRadiusLG` → `--ant-border-radius-lg`): Inline-Stile und Produkt-CSS lesen sie weiter.
 */
export function themeTokenVarDeclarations(token: ThemeToken): string {
  return themeTokenKeys.map((key) => {
    const name = key.replace(/([a-z0-9])([A-Z])/g, "$1-$2").replace(/([A-Z]+)([A-Z][a-z0-9]+)/g, "$1-$2").replace(/([a-z])([A-Z0-9])/g, "$1-$2").toLowerCase();
    const value = token[key];
    return `--ant-${name}:${typeof value === "number" && !UNITLESS_TOKEN.test(key) ? `${value}px` : value};`;
  }).join("");
}

function themeVarsCss(definition: ThemeDefinition): string {
  return `:root{${themeVarNames.map((name) => `--mq-theme-${name}:${definition.vars[name]};`).join("")}${themeTokenVarDeclarations(frozenThemeTokens[definition.id])}}`;
}

/** Setzt Merkmale und CSS-Variablen des Themes am Dokument. */
export function applyThemeToDocument(definition: ThemeDefinition): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.theme = definition.brightness;
  root.dataset.themeId = definition.id;
  root.dataset.themeFamily = definition.family;
  root.dataset.themeField = definition.fieldVariant;
  root.dataset.themeTypography = definition.typography;
  root.dataset.themeLook = definition.look;
  document.body.dataset.theme = definition.brightness;
  root.style.colorScheme = definition.brightness;
  let style = document.getElementById(VAR_STYLE_ID);
  if (!style) {
    style = document.createElement("style");
    style.id = VAR_STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = themeVarsCss(definition);
}

/** Alle Themes des Katalogs in Katalogreihenfolge, gefiltert auf eine Freigabe. */
export function availableThemeDefinitions(availability: ThemeAvailability | null): ThemeDefinition[] {
  if (!availability) return [...themeCatalog];
  return themeCatalog.filter((definition) => availability.availableThemeIds.includes(definition.id));
}

/** Frühere Ablage des `useThemeMode`-Providers (mQmarketing, mQguard): Hell/Dunkel und Primärfarbe. */
export function readStoredModeAndPrimaryThemeId(productDefaultThemeId: ThemeId): ThemeId | null {
  try {
    const mode = localStorage.getItem("mq_theme_mode");
    const primary = localStorage.getItem("mq_theme_primary");
    if (!mode && !primary) return null;
    return legacyThemeId({ mode, primary }, productDefaultThemeId);
  } catch {
    return null;
  }
}
